import React from "react";
import copy from "copy-to-clipboard";
import { toast } from "sonner";
import { Copy, ExternalLink, Monitor, Smartphone } from "lucide-react";
import { Button, Combobox, Input, Label, Tabs, Tooltip } from "@/components/ui";
import { Player } from "@/services/bedrock";
import { PaginatedCombobox } from "@/utils";
import { PreviewDevice } from "./usePreviewPlayer";

const PreviewDeviceTabs: React.FC<{ value: PreviewDevice; onChange: (value: PreviewDevice) => void }> = ({
  value,
  onChange,
}) => (
  <div className="flex flex-col gap-1.5">
    <Label>Preview Device</Label>
    <Tabs value={value} onValueChange={next => onChange(next as PreviewDevice)}>
      <Tabs.List>
        <Tabs.Trigger value="web" className="gap-1.5 px-3">
          <Monitor className="size-3.5" />
          Web
        </Tabs.Trigger>
        <Tabs.Trigger value="mobile" className="gap-1.5 px-3">
          <Smartphone className="size-3.5" />
          Mobile
        </Tabs.Trigger>
      </Tabs.List>
    </Tabs>
  </div>
);

const PreviewPlayerField: React.FC<{ players: PaginatedCombobox<Player, false> }> = ({ players }) => (
  <div className="flex flex-col gap-1.5">
    <Label>Preview Player</Label>
    <Combobox
      {...players}
      showClear
      placeholder="Sample data"
      emptyText="No Players found"
      itemToStringLabel={player => [player.fullName || "No Name", player.email].filter(Boolean).join(" ")}
      itemToLabel={player => (
        <>
          <span className="font-semibold ignore-base-styling">{player.fullName || "No Name"}</span>
          {player.email ? ` - ${player.email}` : ""}
        </>
      )}
    />
  </div>
);

interface UrlFieldProps {
  label: string;
  url: string;
  canOpen: boolean;
}

const UrlField: React.FC<UrlFieldProps> = ({ label, url, canOpen }) => {
  const name = label.charAt(0).toLowerCase() + label.slice(1);
  const handleCopy = () => {
    if (!url) {
      toast.error(`${label} is not available yet`);
      return;
    }
    copy(url);
    toast.success(`${label} copied`);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      <div className="flex gap-2 min-w-0">
        <Input readOnly value={url} className="min-w-0 cursor-default bg-slate-100 text-xs text-slate-500" />
        <Tooltip>
          <Tooltip.Trigger
            render={
              <Button type="button" variant="outline" onClick={handleCopy} aria-label={`Copy ${name}`}>
                <Copy className="h-4 w-4" />
              </Button>
            }
          />
          <Tooltip.Content>Copy {name}</Tooltip.Content>
        </Tooltip>
        {canOpen && (
          <Tooltip>
            <Tooltip.Trigger
              render={
                <Button
                  type="button"
                  variant="outline"
                  disabled={!url}
                  onClick={() => window.open(url, "_blank")}
                  aria-label={`Open ${name}`}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              }
            />
            <Tooltip.Content>Open {name}</Tooltip.Content>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export { PreviewDeviceTabs, PreviewPlayerField, UrlField };
